import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { useNotifications, requestPush } from "@/hooks/useNotifications";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  LayoutDashboard, MessagesSquare, CalendarDays, Users, Settings as SettingsIcon,
  Crown, Bell, LogOut, Menu, Zap, BellRing, CircleDot, ShieldCheck, Lock, Clock, HelpCircle, ClipboardList,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/inbox", label: "Inbox WhatsApp", icon: MessagesSquare, testid: "nav-inbox" },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, testid: "nav-agenda" },
  { to: "/crm", label: "CRM", icon: Users, testid: "nav-crm" },
  { to: "/configuracoes", label: "Configurações", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/planos", label: "Planos", icon: Crown, testid: "nav-plans" },
  { to: "/ajuda", label: "Como usar", icon: HelpCircle, testid: "nav-help" },
];

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-2">
      <div className="grid h-9 w-9 place-items-center rounded-xl bg-cyan-400 text-slate-950 cyan-glow">
        <Zap className="h-5 w-5" strokeWidth={2.6} />
      </div>
      <span className="font-display text-xl font-extrabold tracking-tight text-white">Flow<span className="text-cyan-400">IA</span></span>
    </div>
  );
}

function NavItems({ onNavigate, superadmin, requests }) {
  let items = NAV;
  if (requests && (requests.ai_mode === "assist" || requests.open > 0 || requests.any)) {
    const i = NAV.findIndex((n) => n.to === "/agenda");
    items = [...NAV.slice(0, i + 1), { to: "/pedidos", label: "Pedidos", icon: ClipboardList, testid: "nav-requests", badge: requests.open }, ...NAV.slice(i + 1)];
  }
  if (superadmin) items = [...items, { to: "/admin", label: "Admin FlowIA", icon: ShieldCheck, testid: "nav-admin" }];
  return (
    <nav className="flex flex-col gap-1.5 px-3">
      {items.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          data-testid={n.testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all ${
              isActive
                ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/30"
                : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 border border-transparent"
            }`
          }
        >
          <n.icon className="h-[18px] w-[18px]" />
          {n.label}
          {n.badge > 0 && <span className="ml-auto rounded-full bg-amber-400 px-1.5 text-[10px] font-bold text-slate-950" data-testid="nav-requests-badge">{n.badge}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { count, items, markAllRead } = useNotifications(true);
  const [open, setOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [requests, setRequests] = useState(null);
  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/requests/counts").then(({ data }) => setRequests({ ...data, any: data.open > 0 || data.new_today > 0 || data.scheduled_today > 0 })).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("flowia:requests-changed", load);
    return () => { clearInterval(t); window.removeEventListener("flowia:requests-changed", load); };
  }, [user]);
  useEffect(() => {
    const load = () => api.get("/billing/status").then(({ data }) => setBilling(data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };
  const enablePush = async () => {
    const r = await requestPush();
    if (r === "granted") toast.success("Notificações do navegador ativadas!");
    else if (r === "unsupported") toast.error("Seu navegador não suporta notificações.");
    else toast.info("Permissão de notificação não concedida.");
  };

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-background text-slate-100">
      {/* Sidebar desktop */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-slate-800/80 bg-slate-950/60 backdrop-blur-xl lg:flex">
        <div className="flex h-16 items-center border-b border-slate-800/80">
          <Brand />
        </div>
        <div className="flex-1 overflow-y-auto py-5">
          <NavItems superadmin={user?.is_superadmin} requests={requests} />
        </div>
        <div className="border-t border-slate-800/80 p-3">
          <button
            data-testid="sidebar-logout-btn"
            onClick={doLogout}
            className="flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-slate-400 hover:bg-slate-800/60 hover:text-red-300 transition-all"
          >
            <LogOut className="h-[18px] w-[18px]" /> Sair
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-slate-800/80 bg-slate-950/70 px-4 backdrop-blur-xl sm:px-6">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden text-slate-300" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 border-slate-800 bg-slate-950 p-0">
                <div className="flex h-16 items-center border-b border-slate-800/80"><Brand /></div>
                <div className="py-5"><NavItems onNavigate={() => setOpen(false)} superadmin={user?.is_superadmin} requests={requests} /></div>
              </SheetContent>
            </Sheet>
            <div className="hidden items-center gap-2 text-xs font-mono-flow uppercase tracking-widest text-cyan-400/80 sm:flex">
              <CircleDot className="h-3.5 w-3.5" /> Assistente IA Empresarial
            </div>
          </div>

          <div className="flex items-center gap-2">
            {billing && billing.plan_status === "trial" && !billing.blocked && (
              <button onClick={() => navigate("/planos")} className="hidden items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-xs font-medium text-cyan-300 hover:bg-cyan-400/20 md:flex" data-testid="trial-badge">
                <Clock className="h-3.5 w-3.5" /> Teste grátis · {billing.days_left} dia(s) · Assinar
              </button>
            )}
            {billing && billing.plan_status === "pending" && (
              <span className="hidden items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-300 md:flex" data-testid="pending-badge"><Clock className="h-3.5 w-3.5" /> Pix em confirmação</span>
            )}
            <Button variant="ghost" size="sm" onClick={enablePush} className="hidden text-slate-300 hover:text-cyan-300 sm:flex" data-testid="enable-push-btn">
              <BellRing className="mr-1.5 h-4 w-4" /> Ativar web push
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <button className="relative grid h-10 w-10 place-items-center rounded-xl border border-slate-800 bg-slate-900/60 text-slate-300 hover:text-cyan-300 transition-colors" data-testid="notif-bell-btn">
                  <Bell className="h-5 w-5" />
                  {count > 0 && (
                    <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-cyan-400 px-1 text-[11px] font-bold text-slate-950" data-testid="notif-count">
                      {count}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 border-slate-800 bg-slate-950/95 p-0 backdrop-blur-xl">
                <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
                  <p className="font-display text-sm font-semibold text-white">Notificações</p>
                  <button onClick={markAllRead} className="text-xs text-cyan-400 hover:underline" data-testid="notif-mark-read">Marcar lidas</button>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {items.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-500">Nenhuma nova notificação</p>}
                  {items.map((n) => (
                    <div key={n.id} className="border-b border-slate-800/60 px-4 py-3 hover:bg-slate-900/60" data-testid="notif-item">
                      <p className="text-sm font-medium text-slate-100">{n.title}</p>
                      <p className="mt-0.5 text-xs text-slate-400">{n.body}</p>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/60 py-1.5 pl-1.5 pr-3 transition-colors hover:border-cyan-400/40" data-testid="user-menu-btn">
                  <Avatar className="h-7 w-7">
                    {user?.picture ? <AvatarImage src={user.picture} /> : null}
                    <AvatarFallback className="bg-cyan-400/20 text-xs font-bold text-cyan-300">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden text-sm font-medium text-slate-200 sm:block">{user?.name?.split(" ")[0]}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 border-slate-800 bg-slate-950/95 backdrop-blur-xl">
                <p className="text-sm font-medium text-white">{user?.name}</p>
                <p className="truncate text-xs text-slate-400">{user?.email}</p>
                <div className="my-2 h-px bg-slate-800" />
                <button onClick={enablePush} className="mb-1 flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-slate-300 hover:bg-slate-800 sm:hidden">
                  <BellRing className="h-4 w-4" /> Ativar web push
                </button>
                <button onClick={doLogout} className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-red-300 hover:bg-slate-800" data-testid="menu-logout-btn">
                  <LogOut className="h-4 w-4" /> Sair
                </button>
              </PopoverContent>
            </Popover>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          {billing?.blocked ? (
            <div className="mx-auto mt-10 max-w-lg rounded-2xl border border-amber-500/30 bg-slate-900/70 p-8 text-center glow-card" data-testid="billing-blocked">
              <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-amber-500/15 text-amber-300"><Lock className="h-7 w-7" /></div>
              <h2 className="font-display text-2xl font-bold text-white">{billing.plan_status === "canceled" ? "Assinatura cancelada" : billing.plan_status === "suspended" ? "Assinatura suspensa" : billing.plan_status === "expired" && billing.plan_expires_at ? "Sua assinatura venceu" : "Seu teste grátis terminou"}</h2>
              <p className="mt-2 text-sm text-slate-400">A IA pausou o atendimento e a agenda está em modo leitura. {billing.plan_status === "canceled" || billing.plan_status === "suspended" ? "Fale com o suporte da FlowIA para regularizar sua assinatura." : "Assine um plano para reativar tudo — a liberação acontece assim que o pagamento é confirmado."}</p>
              <Button onClick={() => navigate("/planos", { state: { checkout: billing.plan_status !== "canceled" && billing.plan_status !== "suspended" } })} className="mt-6 bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300" data-testid="billing-blocked-cta">Ver planos</Button>
            </div>
          ) : <Outlet />}
        </main>
      </div>
    </div>
  );
}
